/* Shiftwave-only cart component. No ProductForm hooks or global request overrides. */
(() => {
  if (customElements.get('shiftwave-cart-warranty')) return;
  const variants = new Set(['46662081642713', '46662081675481']);
  const proHandles = new Set(['shiftwave-pro', 'test-shiftwave-pro-new-pricing']);
  const instances = new Set();
  const state = { pending: false, uncertain: false, message: '' };
  const notify = () => instances.forEach(instance => instance.sync());
  const eligible = cart => cart.items.reduce((count, item) => count + (proHandles.has(item.handle) ? item.quantity : 0), 0) === 1
    && !cart.items.some(item => variants.has(String(item.variant_id)));

  async function readCart() {
    const response = await fetch(`${window.theme.routes.root_url}cart.js`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Unable to confirm the current cart.');
    const cart = await response.json();
    if (!Array.isArray(cart.items) || !Number.isInteger(cart.item_count)) throw new Error('Unable to confirm the current cart.');
    return cart;
  }

  function refresh(cart, sections, sectionId) {
    const html = sections?.[sectionId];
    const validSection = typeof html === 'string'
      && new DOMParser().parseFromString(html, 'text/html').getElementById(`MiniCart-${sectionId}`);
    const payload = { source: 'shiftwave-warranty', cart: { ...cart } };
    if (validSection) payload.cart.sections = sections;
    window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, payload);
    if (!validSection) {
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
    }
  }

  customElements.define('shiftwave-cart-warranty', class extends HTMLElement {
    connectedCallback() {
      instances.add(this);
      this.controller = new AbortController();
      this.querySelector('[data-warranty-action]').addEventListener('click', event => {
        event.stopPropagation();
        this.add();
      }, { signal: this.controller.signal });
      this.querySelector('[data-warranty-choice]').addEventListener('change', event => {
        event.stopPropagation();
        this.updatePrice();
      }, { signal: this.controller.signal });
      this.unsubscribe = window.theme.pubsub.subscribe(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, event => {
        if (event.source !== 'shiftwave-warranty' && !state.pending && !state.uncertain) state.message = '';
        if (Array.isArray(event.cart?.items)) this.dataset.eligible = String(eligible(event.cart));
        this.sync();
      });
      this.sync();
      this.updatePrice();
    }

    updatePrice() {
      const price = this.querySelector('[data-warranty-price]');
      const select = this.querySelector('[data-warranty-choice]');
      if (price) price.textContent = select.selectedOptions[0]?.dataset.warrantyPrice || '';
    }

    disconnectedCallback() {
      instances.delete(this);
      this.controller?.abort();
      this.unsubscribe?.();
    }

    sync() {
      const button = this.querySelector('[data-warranty-action]');
      const select = this.querySelector('[data-warranty-choice]');
      this.querySelector('[data-warranty-offer]').hidden = this.dataset.eligible !== 'true' && !state.uncertain;
      button.disabled = state.pending || (!state.uncertain && select.options.length === 0);
      button.setAttribute('aria-busy', String(state.pending));
      button.textContent = state.pending ? 'Please wait…' : state.uncertain ? 'Check cart' : (button.dataset?.warrantyLabel || 'Add warranty');
      select.disabled = state.pending || state.uncertain;
      this.querySelector('[data-warranty-status]').textContent = state.message;
    }

    async add() {
      if (state.pending) return;
      const selected = this.querySelector('[data-warranty-choice]').value;
      if (!state.uncertain && !variants.has(selected)) return;
      state.pending = true;
      state.message = state.uncertain ? 'Checking your cart…' : 'Adding warranty…';
      notify();
      const drawer = this.closest('cart-drawer');
      const sectionId = drawer?.getAttribute('data-section-id');
      const focusWasHere = this.contains(document.activeElement);
      let attempted = false;
      try {
        const cart = await readCart();
        if (state.uncertain) {
          state.uncertain = false;
          state.message = cart.items.some(item => variants.has(String(item.variant_id)))
            ? 'Warranty is in your cart.' : 'Cart checked. No warranty was added; you can try again.';
          refresh(cart, null, sectionId);
          return;
        }
        if (!eligible(cart)) {
          state.message = 'The warranty offer has changed. Please review your cart.';
          refresh(cart, null, sectionId);
          return;
        }
        if (!sectionId) throw new Error('Unable to refresh the cart drawer.');
        attempted = true;
        const response = await fetch(window.theme.routes.cart_add_url, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(selected), quantity: 1 }], sections: [sectionId], sections_url: window.location.pathname })
        });
        const added = await response.json();
        if (!response.ok || added.status) throw new Error('Warranty could not be confirmed.');
        const updated = await readCart();
        const present = updated.items.some(item => String(item.variant_id) === selected);
        state.message = present ? 'Warranty added to your cart.' : 'Warranty was not found. Please review your cart.';
        refresh(updated, added.sections, sectionId);
      } catch (error) {
        if (attempted || state.uncertain) {
          state.uncertain = true;
          try {
            const cart = await readCart();
            state.uncertain = false;
            state.message = cart.items.some(item => variants.has(String(item.variant_id)))
              ? 'Warranty is in your cart. Please review your items.'
              : 'Warranty was not added. Please try again.';
            refresh(cart, null, sectionId);
          } catch {
            state.message = 'We could not confirm the result. Use Check cart before trying another addition.';
          }
        } else {
          state.message = 'Unable to check your cart. Please try again.';
        }
      } finally {
        state.pending = false;
        notify();
        if (focusWasHere && !this.isConnected) {
          const status = drawer?.querySelector('shiftwave-cart-warranty [data-warranty-status]');
          status?.focus({ preventScroll: true });
        }
      }
    }
  });
})();
